import { BulkMessagePriorityMassMessageFilter, BulkMessageStatus } from '@api'
import {
  attach,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'

import { $creatorId } from '@/models/creators.ts'
import { toast } from '@/models/toasts.ts'
import type { MassMessage } from '@/modules/chatting/types.ts'
import { priorityMassMessageFilterTypes } from '@/modules/chatting/ui/sidebar/mass-messages-statistics/constants.ts'
import { mapBulkMessageToMassMessage } from '@/modules/chatting/ui/sidebar/mass-messages-statistics/mappers/mapBulkMessageToMassMessage.ts'
import { massMessageService } from '@/modules/online-mass-messages/service/mass-message-service.ts'
import { handleError } from '@/utils/error'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy.ts'

export const resetPriorityMassMessagesStats = createEvent(
  'reset priority mass message stats',
)
export const changePriorityType = createEvent<string>(
  'change priority mass message stats type',
)
export const $priorityType = createStore<{
  id: string
  value: BulkMessageStatus
}>(priorityMassMessageFilterTypes[0], {
  name: 'priority mass message stats type',
})
  .on(
    changePriorityType,
    (store, id) =>
      priorityMassMessageFilterTypes.find((type) => type.id === id) ?? store,
  )
  .reset(resetPriorityMassMessagesStats)

export const $page = createStore(0, {
  name: 'priority mass message stats page',
}).reset(changePriorityType, resetPriorityMassMessagesStats)

export const fetchPriorityMassMessagesPageFx = attach({
  name: 'priority mass messages fetch page',
  source: {
    creatorId: $creatorId,
    page: $page,
    type: $priorityType,
  },
  mapParams: (_: void, { creatorId, page, type }) => ({
    creatorId: creatorId!,
    page,
    size: 10,
    filters: {
      priorityMassMessageFilter: BulkMessagePriorityMassMessageFilter.ONLY_PMM,
      status: type.value,
      includeStatistic: true,
      includeSendCount: true,
    },
  }),
  effect: createEffect(massMessageService.fetchMassMessages),
})

fetchPriorityMassMessagesPageFx.fail.watch(({ error, params }) => {
  handleError(error, 'Failed to fetch priority mass messages page', () =>
    fetchPriorityMassMessagesPageFx(params),
  )
})

export const unsendMessageFx = attach({
  name: "priority mass message's unsend",
  source: {
    creatorId: $creatorId,
  },
  mapParams: (id: number, { creatorId }) => ({
    creatorId: creatorId!,
    messageId: id,
  }),
  effect: createEffect(massMessageService.withdrawBulkMessage),
})

unsendMessageFx.done.watch(() => {
  toast.success(
    'Successfully ran unsent mass message! It will be unsent in the background.',
  )
})

unsendMessageFx.failData.watch((error) => {
  handleError(error, 'Something went wrong while unsending mass message')
})

export const $stats = createStore<MassMessage[]>([], {
  name: 'priority mass message stats',
})
  .on(fetchPriorityMassMessagesPageFx.doneData, (store, { content }) =>
    content
      ? [
          ...store,
          ...content
            .map((message) => mapBulkMessageToMassMessage(message))
            .filter(isTruthyAndNotEmpty),
        ]
      : store,
  )
  .reset(
    changePriorityType,
    unsendMessageFx.doneData,
    resetPriorityMassMessagesStats,
  )

export const $hasMore = createStore(false, {
  name: 'priority mass message stats has more',
})
  .on(fetchPriorityMassMessagesPageFx.doneData, (_, { hasNext }) => hasNext)
  .reset(
    changePriorityType,
    unsendMessageFx.doneData,
    resetPriorityMassMessagesStats,
  )

$page
  .on(fetchPriorityMassMessagesPageFx.doneData, (page) => page + 1)
  .reset(unsendMessageFx.doneData)

sample({
  clock: [
    $priorityType,
    unsendMessageFx.doneData,
    resetPriorityMassMessagesStats,
  ],
  source: { isLoading: fetchPriorityMassMessagesPageFx.pending },
  filter: ({ isLoading }) => !isLoading,
  target: fetchPriorityMassMessagesPageFx,
})
